import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import {
    Tooltip as UITooltip,
    TooltipContent as UITooltipContent,
    TooltipProvider,
    TooltipTrigger as UITooltipTrigger,
} from '@/components/ui/tooltip'
import { CHART_THEME, METRIC_COLORS } from '@/constants/chartColors'
import { SCHEMES } from '@/components/EchartCustomized'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import AnalyticsIcons from '@/icons/AnalyticsIcons'
import ChartIcons from '@/icons/ChartIcons'
import GridIcons from '@/icons/GridIcons'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import {
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ReferenceArea,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts'
import { Button, IconButton, Typogaphy } from 'vnetwork-ui'
import { getBusinessPerformance } from '../../services'
import {
    createDataHash,
    generateCacheKey,
    getCachedAnalysis,
} from '../../services/aiCache'
import {
    analyzeBusinessPerformance,
    GeminiReportResponse,
} from '../../services/gemini'
import { BusinessPerformanceParams } from '../../types'
import AIAnalysisReport from '../AIAnalysisReport'
import Comparison from './Comparison'

interface BusinessPerformanceTabProps {
    filters?: { gte?: string; lte?: string }
}

type MetricKey =
    | 'net_revenue'
    | 'projected_revenue'
    | 'net_other_income'
    | 'gross_profit'
    | 'operating_profit'
    | 'profit_before_tax'
    | 'net_profit'

interface MetricConfig {
    key: MetricKey
    label: string
    color: string
    description: string
}

const BusinessPerformanceTab: React.FC<BusinessPerformanceTabProps> = ({
    filters,
}) => {
    const { t } = useTranslation()
    const [viewMode, setViewMode] = useState<'chart' | 'table'>('chart')

    // AI Analysis states
    const [aiReport, setAiReport] = useState<GeminiReportResponse | null>(null)
    const [aiLoading, setAiLoading] = useState(false)
    const [aiError, setAiError] = useState<string | null>(null)
    const [aiFromCache, setAiFromCache] = useState(false)
    const [showAiReport, setShowAiReport] = useState(false)

    // Metrics configuration with standardized colors and descriptions
    const metrics: MetricConfig[] = [
        {
            key: 'net_revenue',
            label: t('net_revenue'),
            color: METRIC_COLORS.net_revenue, // Primary Blue
            description: t('net_revenue_description'),
        },
        {
            key: 'projected_revenue',
            label: t('projected_revenue'),
            color: METRIC_COLORS.projected_revenue, // Amber Orange
            description: t('projected_revenue_description'),
        },
        {
            key: 'net_other_income',
            label: t('net_other_income'),
            color: METRIC_COLORS.net_other_income, // Emerald Green
            description: t('net_other_income_description'),
        },
        {
            key: 'gross_profit',
            label: t('gross_profit'),
            color: METRIC_COLORS.gross_profit, // Rose Red
            description: t('gross_profit_description'),
        },
        {
            key: 'operating_profit',
            label: t('operating_profit'),
            color: METRIC_COLORS.operating_profit, // Indigo Purple
            description: t('operating_profit_description'),
        },
        {
            key: 'profit_before_tax',
            label: t('profit_before_tax'),
            color: METRIC_COLORS.profit_before_tax, // Pink Fuchsia
            description: t('profit_before_tax_description'),
        },
        {
            key: 'net_profit',
            label: t('net_profit'),
            color: METRIC_COLORS.net_profit, // Slate Gray
            description: t('net_profit_description'),
        },
    ]

    const params: BusinessPerformanceParams = useMemo(
        () => ({
            from_month: filters?.gte,
            to_month: filters?.lte,
            tax_rate: 0.5,
            include_projected: true,
            breakdown: false,
        }),
        [filters]
    )

    // Fetch data
    const { data, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.REPORTS_BI_BUSINESS_PERFORMANCE, params],
        queryFn: () => getBusinessPerformance(params),
        staleTime: 5 * 60 * 1000,
        placeholderData: true,
    })

    // Visible metrics state
    const [visibleMetrics, setVisibleMetrics] = useState<Set<MetricKey>>(
        new Set(metrics.map((m) => m.key))
    )

    // Auto-load cached AI report when data changes
    useEffect(() => {
        if (!data || data.length === 0) return

        // Check if there's a cached report for this data
        const dataHash = createDataHash(data)
        const cacheKey = generateCacheKey({
            type: 'business_performance',
            period_range: `${filters?.gte || 'N/A'} đến ${filters?.lte || 'N/A'}`,
            selected_metrics: Array.from(visibleMetrics),
            data_hash: dataHash,
        })

        const cachedReport = getCachedAnalysis(cacheKey) as GeminiReportResponse
        if (cachedReport) {
            setAiReport(cachedReport)
            setAiFromCache(true)
            setShowAiReport(true)
            setAiError(null)
        }
    }, [data, filters?.gte, filters?.lte, visibleMetrics])

    // Toggle metric visibility
    const toggleMetric = (key: MetricKey) => {
        setVisibleMetrics((prev) => {
            const newSet = new Set(prev)
            if (newSet.has(key)) {
                newSet.delete(key)
            } else {
                newSet.add(key)
            }
            return newSet
        })
    }

    // Toggle all metrics
    const toggleAll = () => {
        if (visibleMetrics.size === metrics.length) {
            setVisibleMetrics(new Set())
        } else {
            setVisibleMetrics(new Set(metrics.map((m) => m.key)))
        }
    }

    // Prepare chart data with filled missing months
    const chartData = useMemo(() => {
        if (!data || !filters?.gte || !filters?.lte) return []

        // Generate all months in the range
        const allMonths: string[] = []
        const start = dayjs(filters.gte)
        const end = dayjs(filters.lte)
        let current = start

        while (current.isBefore(end) || current.isSame(end, 'month')) {
            allMonths.push(current.format('YYYY-MM'))
            current = current.add(1, 'month')
        }

        // Create a map of existing data
        const dataMap = new Map(data.map((item) => [item.report_month, item]))

        // Fill all months with data or zeros
        return allMonths.map((month) => {
            const existingData = dataMap.get(month)
            return {
                month,
                net_revenue: existingData?.net_revenue || 0,
                projected_revenue: existingData?.projected_revenue || 0,
                net_other_income: existingData?.net_other_income || 0,
                gross_profit: existingData?.gross_profit || 0,
                operating_profit: existingData?.operating_profit || 0,
                profit_before_tax: existingData?.profit_before_tax || 0,
                net_profit: existingData?.net_profit || 0,
            }
        })
    }, [data, filters?.gte, filters?.lte])

    // Calculate minimum value for negative area shading
    const minValue = useMemo(() => {
        if (!chartData.length) return 0

        let min = 0
        chartData.forEach((item) => {
            metrics.forEach((metric) => {
                if (visibleMetrics.has(metric.key)) {
                    const value = item[metric.key]
                    if (value < min) {
                        min = value
                    }
                }
            })
        })
        return min
    }, [chartData, visibleMetrics])

    // Format number for display
    const formatNumber = (num: number) => {
        return new Intl.NumberFormat('vi-VN', {
            maximumFractionDigits: 0,
        }).format(num)
    }

    // Format month for display
    const formatMonth = (monthStr: string | number | undefined) => {
        if (!monthStr) return ''
        const str = String(monthStr)
        const [year, month] = str.split('-')
        const monthNames = [
            'Jan',
            'Feb',
            'Mar',
            'Apr',
            'May',
            'Jun',
            'Jul',
            'Aug',
            'Sep',
            'Oct',
            'Nov',
            'Dec',
        ]
        return `${monthNames[parseInt(month) - 1]} ${year}`
    }

    // Handle AI Analysis
    const handleAIAnalysis = async () => {
        if (!data || data.length === 0) {
            setAiError('Không có dữ liệu để phân tích')
            setShowAiReport(true)
            return
        }

        setShowAiReport(true)
        setAiLoading(true)
        setAiError(null)
        setAiReport(null)
        setAiFromCache(false)

        try {
            // Check cache first for instant result
            const dataHash = createDataHash(data)
            const cacheKey = generateCacheKey({
                type: 'business_performance',
                period_range: `${filters?.gte || 'N/A'} đến ${filters?.lte || 'N/A'}`,
                selected_metrics: Array.from(visibleMetrics),
                data_hash: dataHash,
            })

            const cachedReport = getCachedAnalysis(
                cacheKey
            ) as GeminiReportResponse
            if (cachedReport) {
                setAiReport(cachedReport)
                setAiFromCache(true)
                setAiLoading(false)
                return
            }

            // If no cache, fetch from API
            const report = await analyzeBusinessPerformance({
                data,
                period_range: `${filters?.gte || 'N/A'} đến ${filters?.lte || 'N/A'}`,
                selected_metrics: Array.from(visibleMetrics),
            })
            setAiReport(report)
            setAiFromCache(false)
        } catch (error: any) {
            console.error('AI Analysis Error:', error)
            setAiError(
                error?.message ||
                    'Không thể kết nối với dịch vụ AI. Vui lòng thử lại sau.'
            )
        } finally {
            setAiLoading(false)
        }
    }

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <div className="space-y-2">
            {/* Header with filters and view toggle */}
            <div className="bg-white border border-gray-200 rounded-md p-4">
                <div className="flex items-center justify-between mb-2">
                    <Typogaphy className="font-medium text-base text-primary">
                        {t('business_performance_over_time')}
                    </Typogaphy>
                    <div className="flex gap-2">
                        <Button
                            size="small"
                            color="primary"
                            startIcon={<AnalyticsIcons className="w-4 h-4" />}
                            onClick={handleAIAnalysis}
                            className="flex items-center gap-2"
                            disabled={!data || data.length === 0}
                        >
                            {t('analyze_with_ai')}
                        </Button>
                        <IconButton
                            size="small"
                            color={viewMode === 'chart' ? 'primary' : 'default'}
                            onClick={() => setViewMode('chart')}
                            className="border text-primary"
                        >
                            <ChartIcons />
                        </IconButton>
                        <IconButton
                            size="small"
                            color={viewMode === 'table' ? 'primary' : 'default'}
                            onClick={() => setViewMode('table')}
                            className="border text-primary"
                        >
                            <GridIcons />
                        </IconButton>
                    </div>
                </div>

                {/* Metric Filters */}
                <TooltipProvider delayDuration={200}>
                    <div className="flex flex-wrap gap-4 items-center">
                        {/* Select All */}
                        <div className="flex items-center gap-2">
                            <Checkbox
                                id="select-all"
                                checked={visibleMetrics.size === metrics.length}
                                onCheckedChange={toggleAll}
                            />
                            <Label
                                htmlFor="select-all"
                                className="text-sm cursor-pointer"
                            >
                                {t('select_all')}
                            </Label>
                        </div>

                        {/* Individual Metrics */}
                        {metrics.map((metric) => (
                            <div
                                key={metric.key}
                                className="flex items-center gap-2"
                            >
                                <Checkbox
                                    id={metric.key}
                                    checked={visibleMetrics.has(metric.key)}
                                    onCheckedChange={() =>
                                        toggleMetric(metric.key)
                                    }
                                    style={
                                        visibleMetrics.has(metric.key)
                                            ? {
                                                  backgroundColor: metric.color,
                                                  borderColor: metric.color,
                                              }
                                            : {
                                                  borderColor: metric.color,
                                              }
                                    }
                                />
                                <UITooltip>
                                    <UITooltipTrigger asChild>
                                        <Label
                                            htmlFor={metric.key}
                                            className="text-sm cursor-pointer"
                                            style={{
                                                color: visibleMetrics.has(
                                                    metric.key
                                                )
                                                    ? metric.color
                                                    : undefined,
                                            }}
                                        >
                                            {metric.label}
                                        </Label>
                                    </UITooltipTrigger>
                                    <UITooltipContent
                                        side="top"
                                        className="max-w-xs"
                                    >
                                        <p className="text-xs">
                                            {metric.description}
                                        </p>
                                    </UITooltipContent>
                                </UITooltip>
                            </div>
                        ))}
                    </div>
                </TooltipProvider>
            </div>

            {/* Chart View */}
            {viewMode === 'chart' && (
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <ResponsiveContainer width="100%" height={500}>
                        <LineChart
                            data={chartData}
                            margin={{
                                top: 5,
                                right: 30,
                                left: 20,
                                bottom: 5,
                            }}
                        >
                            <CartesianGrid
                                stroke={CHART_THEME.grid.stroke}
                                strokeDasharray={
                                    CHART_THEME.grid.strokeDasharray
                                }
                            />
                            <XAxis
                                dataKey="month"
                                tickFormatter={formatMonth}
                                tick={{
                                    fontSize: CHART_THEME.axis.fontSize,
                                    fontFamily: CHART_THEME.axis.fontFamily,
                                }}
                                stroke={CHART_THEME.axis.stroke}
                            />
                            <YAxis
                                tickFormatter={(value) =>
                                    new Intl.NumberFormat('vi-VN', {
                                        notation: 'compact',
                                        compactDisplay: 'short',
                                    }).format(value)
                                }
                                tick={{
                                    fontSize: CHART_THEME.axis.fontSize,
                                    fontFamily: CHART_THEME.axis.fontFamily,
                                }}
                                stroke={CHART_THEME.axis.stroke}
                            />
                            <Tooltip
                                contentStyle={CHART_THEME.tooltip.contentStyle}
                                itemStyle={CHART_THEME.tooltip.itemStyle}
                                labelStyle={CHART_THEME.tooltip.labelStyle}
                                formatter={(value: number) =>
                                    formatNumber(value)
                                }
                                labelFormatter={formatMonth}
                            />
                            <Legend
                                wrapperStyle={{
                                    fontSize: CHART_THEME.legend.fontSize,
                                    fontFamily: CHART_THEME.legend.fontFamily,
                                }}
                                iconType="line"
                            />

                            {/* Negative area shading */}
                            {minValue < 0 && (
                                <ReferenceArea
                                    y1={minValue}
                                    y2={0}
                                    fill="url(#diagonalStripes)"
                                    fillOpacity={0.3}
                                />
                            )}

                            {/* SVG Pattern for diagonal stripes */}
                            <defs>
                                <pattern
                                    id="diagonalStripes"
                                    patternUnits="userSpaceOnUse"
                                    width="8"
                                    height="8"
                                    patternTransform="rotate(45)"
                                >
                                    <line
                                        x1="0"
                                        y1="0"
                                        x2="0"
                                        y2="8"
                                        stroke={SCHEMES.mono.gray[2]}
                                        strokeWidth="4"
                                    />
                                </pattern>
                            </defs>

                            {metrics.map((metric) =>
                                visibleMetrics.has(metric.key) ? (
                                    <Line
                                        key={metric.key}
                                        type="monotone"
                                        dataKey={metric.key}
                                        name={metric.label}
                                        stroke={metric.color}
                                        strokeWidth={1}
                                        dot={{ r: 2 }}
                                        activeDot={{ r: 4 }}
                                    />
                                ) : null
                            )}
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            )}

            {/* Table View */}
            {viewMode === 'table' && (
                <div className="bg-white border border-gray-200 rounded-lg p-6 overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-gray-200">
                                <th className="text-left py-3 px-4 font-semibold text-gray-700">
                                    {t('month')}
                                </th>
                                {metrics
                                    .filter((m) => visibleMetrics.has(m.key))
                                    .map((metric) => (
                                        <th
                                            key={metric.key}
                                            className="text-right py-3 px-4 font-semibold text-gray-700"
                                        >
                                            {metric.label}
                                        </th>
                                    ))}
                            </tr>
                        </thead>
                        <tbody>
                            {chartData.map((row, idx) => (
                                <tr
                                    key={idx}
                                    className="border-b border-gray-100 hover:bg-gray-50"
                                >
                                    <td className="py-3 px-4 font-medium">
                                        {formatMonth(row.month)}
                                    </td>
                                    {metrics
                                        .filter((m) =>
                                            visibleMetrics.has(m.key)
                                        )
                                        .map((metric) => (
                                            <td
                                                key={metric.key}
                                                className="text-right py-3 px-4"
                                            >
                                                {formatNumber(row[metric.key])}
                                            </td>
                                        ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {/* AI Analysis Report - Inline */}
            {(showAiReport || aiFromCache) && (
                <AIAnalysisReport
                    report={aiReport}
                    loading={aiLoading}
                    error={aiError}
                    fromCache={aiFromCache}
                />
            )}

            {/* Period Comparison Section */}
            <Comparison metrics={metrics} />
        </div>
    )
}

export default BusinessPerformanceTab
